import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';

import { BusinessDocument, BusinessDocumentWorkspaceModel } from '../../../core/services/business-document.service';
import { BusinessPartnerService } from '../../../core/services/business-partner.service';
import { EmailClientService } from '../../../core/services/email-client.service';
import { EmailAccount, EmailMessage } from '../../../core/services/email-client.types';
import { DocumentPdfService, PdfDocumentPayload } from '../../../core/services/document-pdf.service';

@Component({
  selector: 'app-business-document-email-drawer',
  standalone: false,
  templateUrl: './business-document-email-drawer.html',
  styleUrl: './business-document-email-drawer.scss',
})
export class BusinessDocumentEmailDrawer implements OnChanges {
  @Input({ required: true }) workspace!: BusinessDocumentWorkspaceModel;
  @Input({ required: true }) printablePayload!: PdfDocumentPayload;

  mode: 'list' | 'view' | 'compose' = 'list';
  accounts: EmailAccount[] = [];
  messages: EmailMessage[] = [];
  accountId = '';
  recipient = '';
  subject = '';
  body = '';
  cc = '';
  replyToMessageId: string | null = null;
  selectedMessage: EmailMessage | null = null;
  loading = false;
  sending = false;

  constructor(
    private readonly documents: BusinessDocument,
    private readonly emailClient: EmailClientService,
    private readonly partners: BusinessPartnerService,
    private readonly pdf: DocumentPdfService,
    private readonly snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workspace']?.currentValue) void this.load();
  }

  openComposer(): void {
    this.mode = 'compose';
    this.replyToMessageId = null;
    this.selectedMessage = null;
    this.subject = '';
    this.body = '';
    this.prepareDefaults();
    this.prepareContributorCc();
  }

  showMessages(): void {
    this.mode = 'list';
    this.selectedMessage = null;
  }

  async openMessage(message: EmailMessage): Promise<void> {
    try {
      this.selectedMessage = await firstValueFrom(this.emailClient.getMessage(message.id));
    } catch {
      this.selectedMessage = message;
    }
    this.mode = 'view';
    this.cdr.detectChanges();
  }

  replyToMessage(message: EmailMessage): void {
    this.selectedMessage = message;
    this.replyToMessageId = message.id;
    this.accountId = message.account_id || this.accountId;
    this.recipient = message.from.address;
    const subject = message.subject || this.workspace.document.document_number;
    this.subject = subject.toLowerCase().startsWith('re:') ? subject : `Re: ${subject}`;
    this.body = '';
    this.prepareContributorCc();
    this.mode = 'compose';
  }

  async send(): Promise<void> {
    const recipient = this.recipient.trim();
    if (!recipient || !this.accountId || this.sending) return;
    this.sending = true;
    try {
      await firstValueFrom(this.emailClient.sendMessage({
        account_id: this.accountId,
        to: recipient,
        cc: this.cc.trim() || null,
        bcc: null,
        subject: this.threadedSubject(),
        body: this.body.trim(),
        in_reply_to_message_id: this.replyToMessageId,
        business_document_id: this.workspace.document.id,
        business_document_thread_key: this.threadKey(),
        attachments: this.replyToMessageId ? [] : [{
          filename: this.printablePayload.fileName || `${this.workspace.document.document_number}.pdf`,
          content_type: 'application/pdf',
          base64_data: this.pdf.base64(this.printablePayload),
        }],
      }));
      await this.loadMessages();
      this.mode = 'list';
      this.replyToMessageId = null;
      this.snackBar.open('Document email sent.', 'Close', {
        duration: 3000,
        panelClass: ['success-snackbar'],
      });
    } catch (error: any) {
      this.snackBar.open(error?.error?.message ?? 'The document email could not be sent.', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar'],
      });
    } finally {
      this.sending = false;
      this.cdr.detectChanges();
    }
  }

  async recreateLink(): Promise<void> {
    try {
      this.workspace.out_step_link = await firstValueFrom(
        this.documents.outStepLink(this.workspace.document.id, true),
      );
      this.body = this.defaultBody();
      this.snackBar.open('A replacement public link was created.', 'Close', { duration: 3000 });
      this.cdr.detectChanges();
    } catch (error: any) {
      this.snackBar.open(error?.error?.message ?? 'The public link could not be recreated.', 'Close', {
        duration: 5000,
      });
    }
  }

  private async load(): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    try {
      const [accounts] = await Promise.all([
        firstValueFrom(this.emailClient.listAccounts()),
        this.loadMessages(),
      ]);
      this.accounts = accounts.filter((account) => account.enabled);
      this.accountId = this.accounts.find((account) => account.is_default)?.id
        ?? this.accounts[0]?.id
        ?? '';
      if (this.workspace.document.partner_id && !this.recipient.trim()) {
        try {
          this.recipient = (await this.partners.getPartner(this.workspace.document.partner_id)).email ?? '';
        } catch {
          this.recipient = '';
        }
      }
      this.prepareDefaults();
      this.prepareContributorCc();
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private async loadMessages(): Promise<void> {
    try {
      this.messages = await firstValueFrom(
        this.emailClient.businessDocumentMessages(this.workspace.document.id),
      );
    } catch {
      this.messages = [];
    }
  }

  private prepareDefaults(): void {
    if (!this.subject.trim()) {
      this.subject = `${this.workspace.document.document_number} · ${this.workspace.document.display_name}`;
    }
    if (!this.body.trim()) this.body = this.defaultBody();
  }

  private prepareContributorCc(): void {
    const excluded = new Set([
      this.recipient.trim().toLowerCase(),
      this.accounts.find((account) => account.id === this.accountId)?.email_address.toLowerCase() ?? '',
    ]);
    this.cc = (this.workspace.communication_contributors ?? [])
      .map((contributor) => contributor.email.trim())
      .filter((email, index, all) => email && !excluded.has(email.toLowerCase())
        && all.findIndex((candidate) => candidate.toLowerCase() === email.toLowerCase()) === index)
      .join(', ');
  }

  private defaultBody(): string {
    const step = this.workspace.pipeline.steps
      .find((item) => item.step.id === this.workspace.document.current_step_id)?.step;
    const message = step?.out_step_message
      || `Please review ${this.workspace.document.document_number}.`;
    const link = this.workspace.out_step_link?.url;
    return [
      'Hello,',
      '',
      message,
      ...(link ? ['', `Review and respond: ${link}`] : []),
      '',
      'A PDF copy is attached for your records.',
    ].join('\n');
  }

  private threadKey(): string {
    return this.workspace.document.id.replaceAll('-', '');
  }

  private threadedSubject(): string {
    const clean = this.subject.replace(/\[WW-BD:[^\]]+\]/g, '').trim();
    return `${clean} [WW-BD:${this.threadKey()}]`.trim();
  }
}
